import React, { useCallback } from 'react';
import { Card, Tab } from 'react-bootstrap';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { ClientSurveySearch } from '../../components/ClientSurveys/ClientSurveySearch';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { GridGetterFunction } from '../../../common/components/Grid/GridDataTypes';
import { TabsHistory } from '../../../app/components/TabsHistory';
import { useNavigation } from '../../../common/navigation';
import Button from '../../../common/components/Button/Button';

enum ClientSurveyResultsTabs {
  WITH_ANSWERED = 'withAnswered',
  WITH_ERRORS = 'withErrors',
}

export const ClientSurveyResultsView: React.FC = () => {
  const pok = usePokCore();
  const { t, tk } = useTranslation('administrationLng');
  const nav = useNavigation();
  const lang = pok.getLanguage();

  const getWithAnsweredData = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.clientSurveys.getAllWithAnsweredByPage(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      )) as GridGetterFunction,
    [lang, pok.clientSurveys, t, tk],
  );

  const getWithAnsweredCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.clientSurveys.getAllWithAnsweredCount(filterText, filterColumns),
    [pok.clientSurveys],
  );

  const getWithAnsweredAndErrors = useCallback(
    ((
      pageSize,
      pageNumber,
      orderBy,
      orderDirection,
      filterText,
      filterColumns,
    ) =>
      pok.clientSurveys.getAllWithAnswerAndErrorsByPage(
        t,
        tk,
        lang,
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      )) as GridGetterFunction,
    [lang, pok.clientSurveys, t, tk],
  );

  const getWithAnsweredAndErrorsCount = useCallback(
    (filterText?: string, filterColumns?: string) =>
      pok.clientSurveys.getAllWithAnswerAndErrorsCount(
        filterText,
        filterColumns,
      ),
    [pok.clientSurveys],
  );

  const handleRowClick = (clientSurveyId: string) => {
    nav.surveyAnswerView(clientSurveyId);
  };

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">
          {t(tk.clientSurvey.withAnswered)}
        </Card.Title>
        <TabsHistory
          activeKey={ClientSurveyResultsTabs.WITH_ANSWERED}
          id="clientSurveyResults"
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey={ClientSurveyResultsTabs.WITH_ANSWERED}
            title={t(tk.clientSurvey.withAnswered)}
            className="m-3"
          >
            <ClientSurveySearch
              getData={getWithAnsweredData}
              getCount={getWithAnsweredCount}
              onRowClick={handleRowClick}
              showWarning={true}
              initialOrderBy="clientSurvey.answeredDate"
              initialOrderDirection="DESC"
            />
            <Button
              variant="outline-primary"
              onClick={() => nav.reportSurveyResults()}
            >
              {t(tk.clientSurvey.surveyResultsReport)}
            </Button>
          </Tab>
          <Tab
            eventKey={ClientSurveyResultsTabs.WITH_ERRORS}
            title={t(tk.clientSurvey.withRemarks)}
            className="m-3"
          >
            <ClientSurveySearch
              getData={getWithAnsweredAndErrors}
              getCount={getWithAnsweredAndErrorsCount}
              onRowClick={handleRowClick}
              showWarning={true}
              initialOrderBy="clientSurvey.answeredDate"
              initialOrderDirection="DESC"
            />
            <Button
              variant="outline-primary"
              onClick={() => nav.reportSurveyResultsWithRemarks()}
            >
              {t(tk.clientSurvey.surveyResultsWithRemarksReport)}
            </Button>
          </Tab>
        </TabsHistory>
      </Card.Body>
    </Card>
  );
};
